import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

import BarChart from "@mui/icons-material/BarChart";
import Inventory from "@mui/icons-material/Inventory";
import Category from "@mui/icons-material/Category";
import Lock from "@mui/icons-material/Lock";
import Person from "@mui/icons-material/Person";
import AccountCircle from "@mui/icons-material/AccountCircle";
import Folder from "@mui/icons-material/Folder";
import ReceiptLong from "@mui/icons-material/ReceiptLong";
import Visibility from "@mui/icons-material/Visibility";
import CardGiftcard from "@mui/icons-material/CardGiftcard";
import ReportProblem from "@mui/icons-material/ReportProblem";
import DashboardNotif from "./Function/DashboardNotif";
import { getAuthCredentials } from "../../utils/auth";
import axios from "axios";

import { useTheme } from "@mui/material/styles";
import SecureLS from "secure-ls";
import Swal from "sweetalert2";
import appConfig from "../../config/appConfig";
import TitleSetter from "../../component/TitleSetter";
import StoreTitle from "../../component/StoreTitle";
import ShiftInfo from "./ShiftInfo";
import GrossSalesInfo from "./GrossSalesInfo";

const ls = new SecureLS({ encodingType: "aes" });

const modulesList = [
  { title: "Sales Reports", icon: BarChart, path: "/sales" },
  { title: "Inventory Management", icon: Inventory, path: "/inventory" },
  { title: "Menu Items Creation", icon: Category, path: "/menucreation" },
  { title: "Access", icon: Lock, path: "/access-management" },
  { title: "Vouchers", icon: CardGiftcard, path: "/vouchers" },
  { title: "Reports", icon: Folder, path: "/reports" },
  {
    title: "Configuration",
    icon: ReceiptLong,
    path: "/config",
  },
  {
    title: "Views",
    icon: Visibility,
    path: "/views",
  },
  { title: "Account Management", icon: Person, path: "/account-management" },
  {
    title: "Notification",
    icon: ReportProblem,
    path: "/notifications",
    id: "672312ec5b53fbbe37d4b0d4",
  },
];

const DashboardCard = ({ title, icon: Icon, onClick }) => {
  const theme = useTheme();

  return (
    <Button
      variant="contained"
      onClick={onClick}
      sx={{
        width: "100%",
        padding: 4,
        textAlign: "center",
        color: "#fff",
        backgroundColor: theme.palette.primary.main,
        "&:hover": {
          backgroundColor: theme.palette.primary.dark,
        },
        height: "100%",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          padding: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <Icon sx={{ fontSize: 60, marginBottom: 2 }} />
        <Typography variant="h6">{title}</Typography>
      </Paper>
    </Button>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const user = ls.get("user");
  const companyName = user?.companyname || "POS Client";
  const userRole = user?.role || "Guest";
  const userModules = user?.modules || [];
  const [netStatus, setNetStatus] = useState("Online");
  const fetchDataCalled = useRef(false);
  const [authData, setAuthData] = useState({ posId: null, token: null });

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out and redirected to the login page.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Logout!",
    }).then((result) => {
      if (result.isConfirmed) {
        ls.clear();
        Swal.fire({
          icon: "success",
          title: "Logged Out",
          text: "You have been logged out successfully.",
          confirmButtonText: "OK",
        }).then(() => {
          window.location.href = "/login";
        });
      }
    });
  };

  const formatModuleName = (moduleName) => {
    return moduleName.replace(/([a-z])([A-Z])/g, "$1 $2"); // Add spaces
  };

  const availableModules = modulesList.filter((module) => {
    if (module.title === "Notification") {
      return user?.userId === "672312ec5b53fbbe37d4b0d4";
    }

    if (userRole === "Superadmin") {
      return true;
    }

    const formattedTitle = module.title.replace(/\s/g, "");
    return (
      userModules.includes(formattedTitle) ||
      module.title === "Sales Reports" ||
      module.title === "Account Management"
    );
  });

  useEffect(() => {
    const checkInternetSpeed = () => {
      const startTime = new Date().getTime();
      fetch("https://www.google.com/favicon.ico", { mode: "no-cors" })
        .then(() => {
          const duration = new Date().getTime() - startTime;
          if (duration > 1000) {
            setNetStatus("Slow Net");
          } else {
            setNetStatus("Online");
          }
        })
        .catch(() => setNetStatus("Local"));
    };

    const interval = setInterval(checkInternetSpeed, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (fetchDataCalled.current) return;
    fetchDataCalled.current = true;

    const fetchData = async () => {
      const { user, token, posId } = await getAuthCredentials(navigate);
      setAuthData({ posId, token });

      if (token) {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/receipt/validate-zreadings`,
          { posId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 201) {
          const days = response.data.missingDates.join(", ");
          alert(`Z-Readings missing for the following day(s):\n${days}`);
          window.location.replace("/reports");
        }
      }
    };

    fetchData();
  }, []);

  const netStatusIndicator = {
    Local: { color: "red", label: "Local" },
    "Slow Net": { color: "orange", label: "Slow Net" },
    Online: { color: "green", label: "Online" },
  };

  const dashboardTtile = `${companyName}'s Dashboard`;

  return (
    <Box sx={{ flexGrow: 1, padding: 3 }}>
      <TitleSetter title="Dashboard" />
      {/* <Box 
        sx={{
          position: "absolute",
          top: 10,
          right: { xs: 10, sm: "auto" },
          left: { xs: "auto", sm: 10 },
          display: "flex",
          alignItems: "center",
          opacity: 0.75,
        }}
      >
        <FiberManualRecord
          sx={{
            fontSize: 14,
            color: netStatusIndicator[netStatus].color,
            marginRight: 1,
          }}
        />
        <Typography variant="body2" color="textSecondary">
          {netStatusIndicator[netStatus].label}
        </Typography>
      </Box> */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flexDirection={{ xs: "column", sm: "row" }}
        mb={2}
        sx={{ gap: 2 }}
      >
        <AccountCircle sx={{ fontSize: 100 }} />
        <StoreTitle title={dashboardTtile} />
      </Box>
      <Box
        sx={{
          position: { xs: "relative", sm: "absolute" },
          top: { xs: "auto", sm: 10 },
          right: { xs: "auto", sm: 10 },
          mt: { xs: 1, sm: 0 },
          mb: { xs: 2, sm: 0 },
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <Button variant="contained" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="center"
        alignItems="stretch"
        gap={2}
        mb={2}
      >
        <Box
          flex={{ xs: "1 1 auto", md: 1 }}
          maxWidth={{ xs: "100%", md: 800 }}
          width="100%"
          height="100%"
        >
          <ShiftInfo
            user={user}
            posId={authData.posId}
            token={authData.token}
            navigate={navigate}
          />
        </Box>

        <Box
          flexShrink={0}
          width={{ xs: "100%", md: 300 }}
          display="flex"
          justifyContent="center"
          alignItems="stretch"
        >
          <GrossSalesInfo token={authData.token} />
        </Box>
      </Box>

      <Grid container spacing={3} justifyContent="center" alignItems="stretch">
        {availableModules.map((module) => (
          <Grid
            key={module.title}
            item
            xs={12}
            sm={6}
            md={4}
            sx={{ display: "flex" }}
          >
            <DashboardCard
              title={module.title}
              icon={module.icon}
              onClick={() => navigate(module.path)}
            />
          </Grid>
        ))}
      </Grid>
      <Box
        sx={{
          position: { xs: "absolute", sm: "absolute" },
          top: { xs: 10, sm: "auto" },
          bottom: { xs: "auto", sm: 10 },
          left: 10,
          opacity: 0.75,
        }}
      >
        {/* <Typography
          variant="body2"
          color="textSecondary"
          textAlign={{ xs: "left", sm: "left" }}
        >
          {`${appConfig.appName} Version ${appConfig.appVersion}`}
        </Typography> */}
      </Box>
      <DashboardNotif user={user} />
    </Box>
  );
};

export default Dashboard;
